import type { QueryClient } from '@tanstack/react-query'
import {
  getAllTeamsOptions,
  getAvdelingerOptions,
  getSeksjonerOptions,
  OwnerId,
  type Avdeling,
  type Seksjon,
  type Team,
  type useAuth
} from 'prove-common'
import { collectLeafPaths, emptyOrgLookup, type OrgLookup } from './orgTree'

type AuthFetch = ReturnType<typeof useAuth>['authFetch']

/**
 * Henter organisasjonsstrukturen under en enhet, så langt ned som den går.
 * Kalles først når brukeren fordeler, slik at vi ikke henter hele undertreet
 * bare for å tegne kortene. `fetchQuery` gjenbruker det som allerede ligger i
 * hurtigbufferen, så gjentatte trykk koster ingen nye kall.
 */
export const fetchOrgSubtree = async (
  queryClient: QueryClient,
  authFetch: AuthFetch,
  root: OwnerId,
  teams?: Team[]
): Promise<OrgLookup> => {
  const lookup = emptyOrgLookup()
  lookup.teams =
    teams ?? (await queryClient.fetchQuery(getAllTeamsOptions(authFetch)))

  const loadSeksjoner = async (avdelingId: string): Promise<void> => {
    const seksjoner = await queryClient.fetchQuery(
      getSeksjonerOptions(authFetch, avdelingId)
    )
    lookup.seksjonerByAvdeling.set(
      avdelingId,
      Array.isArray(seksjoner) ? (seksjoner as Seksjon[]) : []
    )
  }

  if (root.type === 'region') {
    const avdelinger = await queryClient.fetchQuery(
      getAvdelingerOptions(authFetch, root.owner)
    )
    const rows = Array.isArray(avdelinger) ? (avdelinger as Avdeling[]) : []
    lookup.avdelingerByRegion.set(root.owner, rows)
    await Promise.all(rows.map((avdeling) => loadSeksjoner(avdeling.id)))
  } else if (root.type === 'avdeling') {
    await loadSeksjoner(root.owner)
  }

  return lookup
}

/**
 * Bladnodene under en enhet, som stier relativt til enheten selv.
 */
export const fetchLeafPaths = async (
  queryClient: QueryClient,
  authFetch: AuthFetch,
  root: OwnerId,
  teams?: Team[]
): Promise<OwnerId[][]> =>
  collectLeafPaths(
    root,
    await fetchOrgSubtree(queryClient, authFetch, root, teams)
  )
